import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Collapse, Stack, Tooltip, styled } from '@mui/material'
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpOutlineIcon,
  OpenInNew as OpenInNewIcon,
  PrintOutlined as PrintOutlinedIcon,
  SpaOutlined as SpaOutlinedIcon,
} from '@mui/icons-material'

import {
  RECORDING_NAV_BLOCK_HINT,
  useActiveRecording,
} from '@/apps/latest/shared/contexts/ActiveRecordingContext'
import { useReleaseChannel } from '@/features/auth'
import { useMyPharmacyQuery } from '@/features/yakureki'
import {
  KAKERU_GUIDE_PDF_URL,
  kakeruGuidePath,
  kakeruGuideVariantOf,
} from '@/pages/guide/companySupport'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_WEIGHT,
  ICON_SIZE,
  scaledFontSize,
} from '@/shared/styles/typography'

import { NAGI_URL, portalNavGroups } from './settingsNavItems'

/**
 * sidebar IconBar active='settings' 時に表示される portal navigation.
 * 旧 PortalSidebar の groupTree + 下部 button を Notion 流に再構成.
 * design/16 §4.4 / M3.c Phase 2.
 */

// 文字サイズモード追従 (ADR-0019): サイドバーは幅が 224 × fontScale で追従するため、
// 設定ナビの文字も scaledFontSize で揃える (styled は theme callback 形 — font-scale.md 禁則).
const Section = styled(Box)(({ theme }) => ({
  padding: '8px 12px',
  fontSize: scaledFontSize('bodySm')(theme),
  color: colorTokens.fg.default,
}))

const GroupHeader = styled(Box, {
  shouldForwardProp: (p) => p !== 'expanded',
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 4px',
  cursor: 'pointer',
  borderRadius: RADIUS.sm,
  color: expanded ? colorTokens.fg.default : colorTokens.fg.muted,
  fontSize: scaledFontSize('tag')(theme),
  fontWeight: FONT_WEIGHT.medium,
  '&:hover': { background: colorTokens.bg.muted },
}))

const ItemRow = styled(Box, {
  shouldForwardProp: (p) => p !== 'active' && p !== 'disabled',
})<{ active: boolean; disabled?: boolean }>(({ theme, active, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 8px 5px 22px',
  borderRadius: RADIUS.sm,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  background: active ? colorTokens.bg.muted : 'transparent',
  color: active ? colorTokens.fg.default : colorTokens.fg.muted,
  fontSize: scaledFontSize('tag')(theme),
  '&:hover': disabled ? undefined : { background: colorTokens.bg.muted },
}))

const LinkSection = styled(Box)({
  padding: '8px 12px',
  borderTop: `1px solid ${colorTokens.border.default}`,
  marginTop: 4,
})

// リンク帯の行は素の <a>。別タブ・アドレスのコピー・link ロールをブラウザに任せる。
// focus の可視化だけは自前で置く (design-book 02-components/sidebar.md リンク帯・
// WorkspaceRow と同じ規律)。
const LinkRow = styled('a')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 4px',
  borderRadius: RADIUS.sm,
  cursor: 'pointer',
  color: colorTokens.fg.muted,
  fontSize: scaledFontSize('tag')(theme),
  textDecoration: 'none',
  '&:hover': { background: colorTokens.bg.muted },
  '&:focus': { outline: 'none' },
  '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
}))

export const SettingsPane = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isActiveRecording } = useActiveRecording()
  // betaOnly 項目 (算定分析) の表示は release_channel 配信で分岐 (新規状態管理なし)
  const { isBeta } = useReleaseChannel()
  // ［困ったときガイド］はログイン中の法人の版へ (アイセイは連絡先が本部サポート
  // デスク)。版を決めるのは back で、ここは返ってきた版名を受けるだけ
  // (design-book ADR-0051)。［印刷用PDF］の版は back が session から解決する。
  const { data: pharmacy } = useMyPharmacyQuery()
  const guideVariant = kakeruGuideVariantOf(pharmacy?.guide_variant)
  // 表示嗜好 (文字サイズ / 改善提案初期開閉) は「設定 > 表示設定」画面へ集約済のため、
  // sidebar からはトグルを撤去 (= 引き算)。
  const [expanded, setExpanded] = useState<string[]>(() =>
    portalNavGroups.map((g) => g.id)
  )

  const toggleGroup = (id: string) => {
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    )
  }

  const handleItemClick = (path: string) => {
    if (isActiveRecording) return
    navigate(path)
  }

  return (
    // 設定ナビは全体を1つの流れとして扱う (固定したい帯が無い). Sidebar 側が
    // スクロールを持たなくなったため、 この pane が自分で縦の溢れを引き受ける.
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <Section sx={{ pt: 1.5 }}>
        <Stack spacing={0.25}>
          {portalNavGroups.map((group) => {
            const isOpen = expanded.includes(group.id)
            return (
              <Box key={group.id}>
                <GroupHeader
                  expanded={isOpen}
                  onClick={() => toggleGroup(group.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                >
                  <Box sx={{ flex: 1 }}>{group.title}</Box>
                  {isOpen ? (
                    <ExpandLessIcon sx={{ fontSize: ICON_SIZE.sm }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: ICON_SIZE.sm }} />
                  )}
                </GroupHeader>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Stack spacing={0.125}>
                    {group.items
                      .filter((item) => !item.betaOnly || isBeta)
                      .map((item) => {
                        const active = location.pathname === item.path
                        const disabled = isActiveRecording
                        return (
                          <Tooltip
                            key={item.id}
                            title={disabled ? RECORDING_NAV_BLOCK_HINT : ''}
                            placement="right"
                            disableHoverListener={!disabled}
                          >
                            <ItemRow
                              active={active}
                              disabled={disabled}
                              role="button"
                              tabIndex={disabled ? -1 : 0}
                              aria-disabled={disabled}
                              aria-current={active ? 'page' : undefined}
                              onClick={() => handleItemClick(item.path)}
                              onKeyDown={(e) => {
                                if (disabled) return
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  handleItemClick(item.path)
                                }
                              }}
                            >
                              {item.title}
                            </ItemRow>
                          </Tooltip>
                        )
                      })}
                  </Stack>
                </Collapse>
              </Box>
            )
          })}
        </Stack>
      </Section>
      {/* リンク帯。外部を別タブで開く行だけを置く (opener は渡さない =
          reverse tabnabbing 対策。AnyCOMPASS 埋め込み中も iframe 内遷移にならない)。
          ガイドはアプリのシェルを被せない独立ページなので、同じタブで出すと戻る手段が
          ブラウザの戻るだけになる。別タブなら録音中も開ける (design-book ADR-0044)。
          困ったときの入口はガイドの 1 行だけで、連絡先はガイドの
          「それでも解決しないとき」が持つ (ADR-0048)。［印刷用PDF］は紙の店舗シート
          (A4 表裏) で、実体は back が S3 から署名付き URL で返す。置き場はここ 1 つで、
          ガイド本文には置かない (ADR-0049) */}
      <LinkSection>
        <Stack spacing={0.25}>
          <LinkRow
            href={kakeruGuidePath(guideVariant)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <HelpOutlineIcon sx={{ fontSize: ICON_SIZE.sm }} />
            <Box sx={{ flex: 1 }}>困ったときガイド</Box>
            <OpenInNewIcon
              sx={{ fontSize: ICON_SIZE.sm, color: colorTokens.fg.faint }}
            />
          </LinkRow>
          <LinkRow
            href={KAKERU_GUIDE_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <PrintOutlinedIcon sx={{ fontSize: ICON_SIZE.sm }} />
            <Box sx={{ flex: 1 }}>印刷用PDF</Box>
            <OpenInNewIcon
              sx={{ fontSize: ICON_SIZE.sm, color: colorTokens.fg.faint }}
            />
          </LinkRow>
          {/* nagi (凪): かかりつけ薬剤師の相談アバターの試作 (今は lab へ飛ばす mock)。
              Kakeru 本体の機能ではなく別 app への外部リンクなので、ナビ tree ではなく
              最下部のリンク帯に置く (= 困ったときガイド と同格の位置づけ)。
              β 限定。合言葉制のため、知らない人が踏んでも中身は開かない。 */}
          {isBeta && (
            <LinkRow href={NAGI_URL} target="_blank" rel="noopener noreferrer">
              <SpaOutlinedIcon sx={{ fontSize: ICON_SIZE.sm }} />
              <Box sx={{ flex: 1 }}>nagi (試作)</Box>
              <OpenInNewIcon
                sx={{ fontSize: ICON_SIZE.sm, color: colorTokens.fg.faint }}
              />
            </LinkRow>
          )}
        </Stack>
      </LinkSection>
    </Box>
  )
}
